import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { isAxiosError } from 'axios';

export type AppendixStatus = 'PENDING' | 'GENERATING' | 'READY' | 'FAILED';

/**
 * 계약 별첨 artifact.
 * 레거시 `contractAppendix.js` 상세 필드와 동일.
 */
export interface ContractAppendixArtifact {
  refYm: string;
  version: number;
  status: AppendixStatus;
  ready?: boolean;
  artifactId?: string;
  fileName?: string;
  fileSizeBytes?: number;
  generatedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  checksumSha256?: string;
  s3Bucket?: string;
  s3Key?: string;
  downloadUrl?: string;
  errorMessage?: string;
}

export interface ContractAppendixDownloadUrl {
  downloadUrl: string;
}

const buildVersionParams = (version?: number) => {
  const params: Record<string, number> = {};
  if (version != null && version >= 1) {
    params.version = version;
  }
  return params;
};

/** 레거시 normalizeArtifacts — 배열·단일 응답을 버전 내림차순 목록으로 정규화 */
export const normalizeAppendixArtifacts = (
  data: ContractAppendixArtifact | ContractAppendixArtifact[] | null | undefined,
): ContractAppendixArtifact[] => {
  if (Array.isArray(data)) {
    return [...data].sort(
      (a, b) => (b.version ?? 0) - (a.version ?? 0),
    );
  }
  if (data) {
    return [data];
  }
  return [];
};

export const contractService = {
  /**
   * 기준월 별첨 목록 조회.
   * 레거시: GET `/admin/contracts/appendices/{refYm}` (version 쿼리 없음)
   */
  getAppendixArtifacts: async (
    refYm: string,
  ): Promise<ContractAppendixArtifact[]> => {
    try {
      const response = await apiClient.get(
        createApiPath.contracts.appendix(refYm),
      );
      return normalizeAppendixArtifacts(response.data);
    } catch (error) {
      // 레거시: 404는 빈 목록으로 취급
      if (isAxiosError(error) && error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  generateAppendix: async (
    refYm: string,
    version = 1,
  ): Promise<ContractAppendixArtifact> => {
    const response = await apiClient.post(
      createApiPath.contracts.appendixGenerate(refYm),
      null,
      { params: buildVersionParams(version) },
    );
    return response.data;
  },

  getAppendixDownloadUrl: async (
    refYm: string,
    version = 1,
  ): Promise<ContractAppendixDownloadUrl> => {
    const response = await apiClient.get(
      createApiPath.contracts.appendixDownloadUrl(refYm),
      { params: buildVersionParams(version) },
    );
    return response.data;
  },
};
